import { Minus, Plus, RotateCcw } from 'lucide-react';
import { ZOOM_CONFIG } from '@/constants/timeline';

export interface ZoomControlsProps {
  /** Current zoom level (0.5 to 3.0) */
  zoom: number;
  /** Callback when zoom changes */
  onZoomChange: (zoom: number) => void;
  /** Minimum zoom level (default: ZOOM_CONFIG.MIN) */
  min?: number;
  /** Maximum zoom level (default: ZOOM_CONFIG.MAX) */
  max?: number;
  /** Zoom step increment (default: ZOOM_CONFIG.STEP) */
  step?: number;
  /** Optional additional className */
  className?: string;
}

/**
 * ZoomControls atom - horizontal zoom controls for timeline
 *
 * Provides minus/plus buttons with percentage display and reset button.
 * Uses consistent styling with other timeline controls (GroupModeToggle).
 */
export function ZoomControls({
  zoom,
  onZoomChange,
  min = ZOOM_CONFIG.MIN,
  max = ZOOM_CONFIG.MAX,
  step = ZOOM_CONFIG.STEP,
  className = '',
}: ZoomControlsProps) {
  const canZoomOut = zoom > min;
  const canZoomIn = zoom < max;
  const isDefault = zoom === ZOOM_CONFIG.DEFAULT;

  const handleZoomOut = () => {
    if (canZoomOut) {
      const newZoom = Math.max(min, zoom - step);
      onZoomChange(Math.round(newZoom * 100) / 100); // Avoid floating point errors
    }
  };

  const handleZoomIn = () => {
    if (canZoomIn) {
      const newZoom = Math.min(max, zoom + step);
      onZoomChange(Math.round(newZoom * 100) / 100);
    }
  };

  const handleReset = () => {
    onZoomChange(ZOOM_CONFIG.DEFAULT);
  };

  const percentage = Math.round(zoom * 100);

  return (
    <div
      className={`
        flex items-center gap-1 rounded-lg bg-bg-muted p-1
        ${className}
      `}
    >
      {/* Zoom out button */}
      <button
        onClick={handleZoomOut}
        disabled={!canZoomOut}
        className="
          flex size-8 items-center justify-center rounded-md text-text-muted transition-colors
          enabled:hover:bg-bg-elevated enabled:hover:text-text-primary
          enabled:active:bg-bg-base
          disabled:cursor-not-allowed disabled:opacity-40
        "
        title="Zoom out"
        aria-label="Zoom out"
      >
        <Minus className="size-4" />
      </button>

      {/* Zoom percentage display */}
      <span
        className="min-w-14 text-center text-sm font-medium text-text-secondary tabular-nums"
        title={`Zoom: ${percentage}%`}
      >
        {percentage}%
      </span>

      {/* Zoom in button */}
      <button
        onClick={handleZoomIn}
        disabled={!canZoomIn}
        className="
          flex size-8 items-center justify-center rounded-md text-text-muted transition-colors
          enabled:hover:bg-bg-elevated enabled:hover:text-text-primary
          enabled:active:bg-bg-base
          disabled:cursor-not-allowed disabled:opacity-40
        "
        title="Zoom in"
        aria-label="Zoom in"
      >
        <Plus className="size-4" />
      </button>

      {/* Reset button (only visible when not at default) */}
      <button
        onClick={handleReset}
        disabled={isDefault}
        className="
          flex size-8 items-center justify-center rounded-md text-text-muted transition-colors
          enabled:hover:bg-bg-elevated enabled:hover:text-text-primary
          enabled:active:bg-bg-base
          disabled:cursor-default disabled:opacity-0
        "
        title="Reset zoom to 100%"
        aria-label="Reset zoom"
      >
        <RotateCcw className="size-3.5" />
      </button>
    </div>
  );
}
